// @ts-strict-ignore
import React, { useMemo } from 'react';
import {
  Box,
  Checkbox,
  Icon,
  IconName,
  Text,
} from '@usealloy/component-library';
import { snakeToCaps } from '~/helpers/formatting';
import {
  checkAllChildren,
  countChildren,
  countSelected,
  filterEmptyObjectsFromEntries,
  formatArrayText,
  getCollapseChecked,
  shouldBeCollapsible,
} from './helpers';
import MetadataCollapse from './MetadataCollapse';
import MetadataCheckbox from './MetadataCheckbox';
import { NoDataWrapper } from './styles';

type SetTabCountCallback = (value: number) => number;

interface MetadataContentProps {
  currentTab: string;
  data: object;
  setTabCount: (operator: SetTabCountCallback) => void;
  setWhitelist: (object) => void;
  whitelist: any;
}

const MetadataContent = ({
  currentTab,
  data,
  setTabCount,
  setWhitelist,
  whitelist,
}: MetadataContentProps) => {
  const numberOfChildren = useMemo(
    () => countChildren(data),
    [data],
  );
  const numberOfSelectedChildren = useMemo(
    () => countSelected(whitelist, data),
    [whitelist, data],
  );

  if (typeof data === 'object' && whitelist === true) {
    checkAllChildren(data, setWhitelist, true);
  }
  const allCheckedState = getCollapseChecked(data, whitelist);
  const lastCheckedKey = Object.keys(whitelist).filter(key => typeof whitelist[key] !== 'object' && whitelist[key]).pop();
  return Object.keys(data).length
    ? (
      <Box
        direction="column"
        alignItems="flex-start"
      >
        <Checkbox
          data-testid="checkbox-all"
          checked={allCheckedState === 'checked'}
          indeterminate={allCheckedState === 'indeterminate'}
          onChange={event => {
            checkAllChildren(data, setWhitelist, event.target.checked);
            setTabCount(value => (
              event.target.checked
                ? value + (numberOfChildren - numberOfSelectedChildren)
                : value - numberOfSelectedChildren
            ));
          }}
          label="All"
          style={{ padding: '0.75rem 0.5rem' }}
        />
        {
          Object.entries(data).filter(filterEmptyObjectsFromEntries).map(([key, value]) => (
            shouldBeCollapsible(value)
              ? (
                <MetadataCollapse
                  key={`metadata-tab-${currentTab}-content-${key}-collapse`}
                  header={snakeToCaps(key)}
                  values={value}
                  setTabCount={setTabCount}
                  setWhitelist={
                    collapseValues => { setWhitelist({ ...whitelist, [key]: collapseValues }); }
                  }
                  whitelist={whitelist?.[key] || {}}
                />
              ) : (
                <MetadataCheckbox
                  key={`metadata-tab-${currentTab}-content-${key}-checkbox`}
                  checked={whitelist?.[key]}
                  handleHideParentKeys={() => {
                    setWhitelist({
                      ...whitelist,
                      [key]: whitelist?.[key] === 'top' ? true : 'top',
                    });
                  }}
                  handleToggle={checked => {
                    if (!checked) {
                      delete whitelist[key];
                      setTabCount(count => count - 1);
                      setWhitelist({ ...whitelist });
                    } else {
                      setWhitelist({ ...whitelist, [key]: checked });
                      setTabCount(count => count + 1);
                    }
                  }}
                  hideParentKeys={whitelist?.[key] === 'top'}
                  label={key}
                  showToggle={key === lastCheckedKey}
                  value={Array.isArray(value) ? formatArrayText(value) : value}
                />
              )
          ))
        }
      </Box>
    ) : (
      <NoDataWrapper
        alignItems="center"
        direction="column"
        justifyContent="center"
      >
        <Icon icon={IconName.CircleClose} height="68" width="68" />
        <Text size={3}>No Data Available</Text>
      </NoDataWrapper>
    );
};

export default MetadataContent;
